import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck } from "lucide-react";

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from "@/api/notifications";
import {
  listAgentNotifications,
  markAgentNotificationRead,
  type AgentNotificationRead,
} from "@/api/agents";
import { agentIcon } from "@/components/agents/agentMeta";
import { openWs } from "@/lib/ws";

/**
 * Localized "x minutes ago" without pulling in a date library — the exact
 * wording per locale lives in the translation files (notifications.*Ago).
 */
function useTimeAgo() {
  const { t } = useTranslation();
  return (iso: string): string => {
    // Backend sends naive UTC timestamps; without a trailing "Z" JS would
    // parse them as local time and always show "just now".
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
    const then = new Date(hasZone ? iso : `${iso}Z`).getTime();
    if (Number.isNaN(then)) return "—";
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return t("notifications.justNow");
    if (diff < 3600)
      return t("notifications.minutesAgo", { count: Math.floor(diff / 60) });
    if (diff < 86400)
      return t("notifications.hoursAgo", { count: Math.floor(diff / 3600) });
    return t("notifications.daysAgo", { count: Math.floor(diff / 86400) });
  };
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timeAgo = useTimeAgo();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [agentItems, setAgentItems] = useState<AgentNotificationRead[]>([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const refresh = async () => {
    try {
      const [res, agents] = await Promise.all([
        listNotifications(),
        listAgentNotifications({ limit: 20 }).catch(() => [] as AgentNotificationRead[]),
      ]);
      setItems(res.items);
      setAgentItems(agents);
      const agentUnread = agents.filter((a) => !a.is_read).length;
      setUnread(res.unread_count + agentUnread);
    } catch {
      // ignore — likely unauthenticated
    }
  };

  useEffect(() => {
    refresh();
    // Keep the slow poll as a safety net — if the WS drops and we miss the
    // intervening message, the poll still catches us up on re-focus. At
    // 5 minutes it's cheap. Skip the tick when the tab is hidden so we
    // don't pile up background work that contributed to renderer OOMs
    // ("Aw, Snap! Error code: 5") on long-idle tabs.
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refresh();
    }, 5 * 60_000);
    // When the tab regains focus after a long idle, refresh immediately so
    // the bell isn't showing stale state.
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Live push: every new notification the backend creates arrives here.
    const ws = openWs({
      path: "/ws/notifications",
      onMessage: (msg) => {
        if (msg.kind === "notification") {
          // Re-pull the list so order / is_read state stays authoritative
          // rather than mutating the local array with the pushed payload.
          void refresh();
        }
      },
    });
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
      ws.close();
    };
  }, []);

  // Close on outside click + Escape key
  useEffect(() => {
    if (!open) return;
    const clickHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", clickHandler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", clickHandler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const handleAgentClick = async (a: AgentNotificationRead) => {
    if (!a.is_read) {
      try {
        await markAgentNotificationRead(a.id);
      } catch {
        // ignore
      }
    }
    setOpen(false);
    await refresh();
    navigate(`/agents/${a.agent_name}`);
  };

  const handleItemClick = async (n: Notification) => {
    if (!n.is_read) {
      try {
        await markNotificationRead(n.id);
      } catch {
        // ignore
      }
    }
    setOpen(false);
    await refresh();
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      // Agent notifications have no bulk endpoint — mark each unread one.
      await Promise.all(
        agentItems
          .filter((a) => !a.is_read)
          .map((a) => markAgentNotificationRead(a.id).catch(() => undefined))
      );
      await refresh();
    } catch {
      // ignore
    }
  };

  const unreadLabel =
    unread > 0
      ? t("notifications.unreadCountLabel", { count: unread })
      : t("notifications.title");

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unreadLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        // Pill matches the search button + theme-toggle in TopBar so
        // the right-hand action cluster reads as one family.
        className="relative w-[38px] h-[38px] rounded-xl border border-border bg-surface/80 text-text-muted hover:text-text-primary hover:bg-surface-muted flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Bell className="h-[17px] w-[17px]" aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            // Glow halo on the badge so the bell catches peripheral
            // vision — same trick the new sidebar uses on its dot
            // indicator.
            className="absolute top-1 right-1 min-w-[1rem] h-4 px-1 text-[10px] font-semibold bg-danger text-white rounded-full flex items-center justify-center shadow-[0_0_6px_rgba(239,68,68,0.5)]"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t("notifications.title")}
          className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-surface shadow-lg overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              {t("notifications.title")}
            </h3>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="flex items-center gap-1 text-xs text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
              >
                <CheckCheck className="h-3 w-3" aria-hidden="true" />{" "}
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {agentItems.map((a) => {
              const AgentIcon = agentIcon(a.agent_name);
              const agentLabel = a.agent_name.charAt(0).toUpperCase() + a.agent_name.slice(1);
              return (
                <button
                  key={`agent-${a.id}`}
                  type="button"
                  onClick={() => handleAgentClick(a)}
                  className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-background transition-colors focus:outline-none focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset ${
                    !a.is_read ? "bg-accent/5" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <AgentIcon
                      className="h-4 w-4 text-accent mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-text-primary">
                          <span className="text-accent">{agentLabel}:</span>{" "}
                          {a.title}
                        </p>
                        {!a.is_read && (
                          <span
                            aria-hidden="true"
                            className="mt-1 flex-shrink-0 inline-block h-2 w-2 rounded-full bg-accent"
                          />
                        )}
                      </div>
                      {a.summary && (
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                          {a.summary}
                        </p>
                      )}
                      <p className="text-[10px] text-text-muted mt-1">
                        {timeAgo(a.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
            {items.length === 0 && agentItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-text-muted">
                {t("notifications.empty")}
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleItemClick(n)}
                  aria-label={
                    !n.is_read
                      ? t("notifications.unreadPrefix", { title: n.title })
                      : n.title
                  }
                  className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-background transition-colors focus:outline-none focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset ${
                    !n.is_read ? "bg-accent/5" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-text-primary">
                      {n.title}
                    </p>
                    {!n.is_read && (
                      <span
                        aria-hidden="true"
                        className="mt-1 flex-shrink-0 inline-block h-2 w-2 rounded-full bg-accent"
                      />
                    )}
                  </div>
                  {n.body && (
                    <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                      {n.body}
                    </p>
                  )}
                  <p className="text-[10px] text-text-muted mt-1">
                    {timeAgo(n.created_at)}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
